import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { YStack, Text, Button } from "tamagui";
import { RiErrorWarningLine, RiTimeLine } from "react-icons/ri";
import { colors } from "@styles/colors";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { handleAPIBindResult, type BindResult } from "../../API/handlePaymentAPI";
import { nextBind3dsAction, resolveRedirectPath, BIND_3DS_POLL_CAP_MS, BIND_3DS_POLL_INTERVAL_MS } from "@utils/bind3dsPoll";
import Processing from "./Processing";

/**
 * 3DS challenge 落地頁（§6）：TapPay `frontend_redirect_url` 導回目標。
 * 讀 `?bind_ref` → 輪詢 GET bind-result（2s 間隔、90s 上限）→
 *   succeeded + pay_order → 導回付款頁繼續付款；succeeded + wallet(或無 context) → 卡片列表；
 *   failed → 顯示原因 + 換卡重試；pending 續輪詢；逾時/expired → 「驗證逾時，請重試」。
 * 決策邏輯抽成純函式 `nextBind3dsAction`（@utils/bind3dsPoll）方便測試，這裡只負責輪詢 + 畫面。
 */
export default function Bind3dsLanding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { cusJwtToken } = useCusAuth();
  // bind_ref 優先讀 URL query（相容舊流程），否則讀跳轉前存的 localStorage
  // （TapPay 導回的 frontend_redirect_url 不帶 query，避免 backend_notify 不送）。
  const bindRef = searchParams.get("bind_ref") ?? localStorage.getItem("bind_3ds_ref");

  const [phase, setPhase] = useState<"missing_ref" | "polling" | "failed" | "timeout" | "error">(bindRef ? "polling" : "missing_ref");
  const [failReason, setFailReason] = useState<string | null>(null);

  const startedAtRef = useRef<number>(Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  useEffect(() => {
    if (!bindRef || !cusJwtToken) return;

    let cancelled = false;

    const poll = async () => {
      const res = await handleAPIBindResult(bindRef, cusJwtToken);
      if (cancelled) return;

      const elapsed = Date.now() - startedAtRef.current;
      const result: BindResult | null = res.state === "success" ? res.data : null;
      const action = nextBind3dsAction(result, elapsed, BIND_3DS_POLL_CAP_MS);

      // 到終態就清掉暫存的 bind_ref（poll 續輪詢時保留）。
      if (action !== "poll") localStorage.removeItem("bind_3ds_ref");

      switch (action) {
        case "redirect": {
          clearTimer();
          const path = resolveRedirectPath(result?.return_context);
          if (path) window.location.href = path;
          break;
        }
        case "wallet":
          clearTimer();
          navigate("/account/paymentMethod", { replace: true });
          break;
        case "fail":
          clearTimer();
          setFailReason(result?.fail_reason ?? null);
          setPhase("failed");
          break;
        case "timeout":
          clearTimer();
          setPhase("timeout");
          break;
        case "poll":
        default:
          break;
      }
    };

    poll();
    timerRef.current = setInterval(poll, BIND_3DS_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimer();
    };
  }, [bindRef, cusJwtToken, navigate]);

  const retry = () => navigate("/account/paymentMethod", { replace: true });

  if (phase === "missing_ref") {
    return (
      <YStack bg={colors.bg} f={1} ai="center" jc="center" p="0 32px" gap="$4">
        <RiErrorWarningLine size={40} color={colors.danger} />
        <Text fontSize={16} fontWeight="700" color={colors.grayDark} ta="center">
          找不到綁卡驗證資訊
        </Text>
        <Button onPress={retry} bg={colors.primary} bw={0} br={12} p="$3" h="auto" w="100%">
          <Text fontSize={14} fontWeight="700" color="#fff">
            返回付款方式
          </Text>
        </Button>
      </YStack>
    );
  }

  if (phase === "failed") {
    return (
      <YStack bg={colors.bg} f={1} ai="center" jc="center" p="0 32px" gap="$4">
        <RiErrorWarningLine size={40} color={colors.danger} />
        <Text fontSize={16} fontWeight="700" color={colors.grayDark} ta="center">
          綁卡驗證失敗
        </Text>
        <Text fontSize={13} color={colors.gray200} ta="center">
          {failReason || "銀行驗證未通過，請確認卡片資訊或更換其他信用卡。"}
        </Text>
        <Button onPress={retry} bg={colors.primary} bw={0} br={12} p="$3" h="auto" w="100%">
          <Text fontSize={14} fontWeight="700" color="#fff">
            換卡重試
          </Text>
        </Button>
      </YStack>
    );
  }

  if (phase === "timeout") {
    return (
      <YStack bg={colors.bg} f={1} ai="center" jc="center" p="0 32px" gap="$4">
        <RiTimeLine size={40} color={colors.amber} />
        <Text fontSize={16} fontWeight="700" color={colors.grayDark} ta="center">
          驗證逾時，請重試
        </Text>
        <Button onPress={retry} bg={colors.primary} bw={0} br={12} p="$3" h="auto" w="100%">
          <Text fontSize={14} fontWeight="700" color="#fff">
            重新綁卡
          </Text>
        </Button>
      </YStack>
    );
  }

  return <Processing message={"驗證中…\n請勿關閉此頁面"} />;
}
